'use client'

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { siteConfig } from '@/config/site'
import { toast } from '@/lib/toast'
import { trackMetaPixel } from '@/lib/meta-pixel'

function validateEmail(email: string) {
  return /\S+@\S+\.\S+/.test(email)
}

export function ContactForm() {
  const [form, setForm] = React.useState({
    name: '',
    email: '',
    company: '',
    goal: '',
    consent: true,
  })
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [loading, setLoading] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value, type } = event.target
    const nextValue =
      type === 'checkbox' && event.target instanceof HTMLInputElement ? event.target.checked : value
    setForm((prev) => ({ ...prev, [name]: nextValue }))
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const currentErrors: Record<string, string> = {}
    if (!form.name.trim()) currentErrors.name = 'Ingresa tu nombre'
    if (!validateEmail(form.email)) currentErrors.email = 'Correo no válido'
    if (!form.company.trim()) currentErrors.company = 'Escribe el nombre de tu empresa'
    if (form.goal.length < 20) currentErrors.goal = 'Explícanos con al menos 20 caracteres'

    setErrors(currentErrors)
    if (Object.keys(currentErrors).length > 0) return

    setLoading(true)
    setSubmitError(null)

    void (async () => {
      try {
        const url = new URL(
          `${process.env.NEXT_PUBLIC_DASHBOARD_API_URL ?? 'http://localhost:4000/api'}`
        )
        url.pathname = `${url.pathname.replace(/\/$/, '')}/v1/leads`

        const searchParams = new URLSearchParams(window.location.search)
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            company: form.company,
            goal: form.goal,
            consent: form.consent,
            source: 'portal_contact',
            urlPath: window.location.pathname + window.location.search,
            utmSource: searchParams.get('utm_source') ?? undefined,
            utmMedium: searchParams.get('utm_medium') ?? undefined,
            utmCampaign: searchParams.get('utm_campaign') ?? undefined,
            utmTerm: searchParams.get('utm_term') ?? undefined,
            utmContent: searchParams.get('utm_content') ?? undefined,
          }),
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.error ?? 'No pudimos guardar tu solicitud')
        }

        toast.success('Gracias, te contactaremos pronto', {
          description: 'Nuestro equipo revisará tu solicitud y agenda una llamada.',
        })
        trackMetaPixel('Lead', {
          content_name: 'Contacto',
          source: 'portal_contact',
        })
        setForm({ name: '', email: '', company: '', goal: '', consent: true })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error inesperado'
        setSubmitError(message)
        toast.error('No pudimos enviar el formulario', {
          description: message,
        })
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <Card
      opaque
      className="border-border/80 bg-card/95 shadow-primary/5 dark:border-border/60 border p-8 shadow-sm"
    >
      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-6 sm:grid-cols-2">
          <Field>
            <Label htmlFor="name">Nombre completo</Label>
            <Input
              id="name"
              name="name"
              autoComplete="off"
              value={form.name}
              onChange={handleChange}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            <FieldError id="name-error">{errors.name}</FieldError>
          </Field>
          <Field>
            <Label htmlFor="email">Correo</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="off"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={form.email}
              onChange={handleChange}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            <FieldError id="email-error">{errors.email}</FieldError>
          </Field>
        </div>
        <Field>
          <Label htmlFor="company">Empresa</Label>
          <Input
            id="company"
            name="company"
            autoComplete="off"
            value={form.company}
            onChange={handleChange}
            aria-invalid={Boolean(errors.company)}
            aria-describedby={errors.company ? 'company-error' : undefined}
          />
          <FieldError id="company-error">{errors.company}</FieldError>
        </Field>
        <Field>
          <Label htmlFor="goal">¿Qué quieres lograr con LuckAgents?</Label>
          <Textarea
            id="goal"
            name="goal"
            value={form.goal}
            onChange={handleChange}
            rows={5}
            autoComplete="off"
            aria-invalid={Boolean(errors.goal)}
            aria-describedby={errors.goal ? 'goal-error' : undefined}
          />
          <p className="text-foreground/60 text-xs">Mínimo 20 caracteres.</p>
          <FieldError id="goal-error">{errors.goal}</FieldError>
        </Field>
        <div className="border-border/60 bg-background/80 flex items-start gap-3 rounded-lg border p-4">
          <input
            id="consent"
            name="consent"
            type="checkbox"
            checked={form.consent}
            onChange={handleChange}
            className="border-border text-primary focus-visible:outline-ring mt-0.5 size-4 rounded focus-visible:outline focus-visible:outline-[3px]"
          />
          <div className="text-foreground/80 space-y-1 text-sm">
            <Label htmlFor="consent" className="font-medium">
              Acepto comunicaciones sobre el producto
            </Label>
            <p className="text-foreground/60 text-xs">
              1-2 mensajes al mes con mejores prácticas y novedades.
            </p>
          </div>
        </div>
        <div className="border-primary/30 bg-primary/5 text-foreground/70 rounded-xl border p-4 text-sm">
          <p className="text-primary font-semibold">Buenas prácticas WhatsApp</p>
          <p className="mt-2">{siteConfig.whatsappNotes.templates}</p>
        </div>
        {submitError ? (
          <p className="text-destructive text-sm" role="alert" aria-live="polite">
            {submitError}
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? 'Enviando…' : 'Enviar'}
        </Button>
        <div role="status" aria-live="polite" className="text-foreground/60 text-xs">
          Respondemos en ~24 horas hábiles.
        </div>
      </form>
    </Card>
  )
}

type FieldProps = { children: React.ReactNode }

function Field({ children }: FieldProps) {
  return <div className="space-y-2">{children}</div>
}

type FieldErrorProps = {
  id: string
  children?: React.ReactNode
}

function FieldError({ id, children }: FieldErrorProps) {
  if (!children) return null
  return (
    <p id={id} className="text-destructive text-sm" role="status" aria-live="polite">
      {children}
    </p>
  )
}
